/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from "react";

import { getCompanyApplication } from "EmployerApplication/Selectors";
import isIntuit from "Intuit/isIntuit";
import { uniq } from "lodash";
import { useForm } from "react-final-form";
import { useSelector } from "react-redux";
import Select, { components } from "react-select";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { BodyMedium } from "Cobalt/BodyText";
import BodyText from "Common/BodyText";
import { Div } from "Common/HTML";

import makeBem from "Utils/makeBem";

const bem = makeBem("NAICSCodeSelectSearch");

const NAICSCodeSelectSearch = ({ shouldPrefillNAICSCode }) => {
  const [naicsCodes, setNaicsCodes] = useState(null);
  const [oshaSicCodes, setOshaSicCodes] = useState(null);
  const [options, setOptions] = useState([]); // initialize as empty array
  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const form = useForm();
  const companyApplication = getCompanyApplication(useSelector((state) => state));

  useEffect(() => {
    import("EmployerApplication/Components/NAICSCodes")
      .then((module) => {
        setNaicsCodes(module.NAICS_CODES);
      })
      .catch((err) => console.error("Error loading NAICS_CODES:", err));

    import("EmployerApplication/Components/SICCodes")
      .then((module) => {
        setOshaSicCodes(module.OSHA_SIC_CODES);
      })
      .catch((err) => console.error("Error loading OSHA_SIC_CODES:", err));
  }, []);

  useEffect(() => {
    if (naicsCodes) {
      setOptions(naicsCodes);
    }
  }, [naicsCodes]);

  useEffect(() => {
    if (shouldPrefillNAICSCode && naicsCodes && companyApplication?.naicsCode && !selectedOption) {
      const NAICSCodeDetails = naicsCodes.find((naicsItem) => naicsItem.naics === companyApplication.naicsCode);
      if (NAICSCodeDetails) {
        setSelectedOption(NAICSCodeDetails);
        setInputValue(`${NAICSCodeDetails.naics}`);
      }
    }
  }, [shouldPrefillNAICSCode, naicsCodes, companyApplication, selectedOption]);

  const getOSHAData = (sic) => {
    return oshaSicCodes ? oshaSicCodes.find((record) => record.sic_code === sic) : null;
  };

  const highlightMatchingTerm = (text, term) => {
    if (!text) return "";
    if (!term) return text;
    const parts = text.split(new RegExp(`(${term.replace(/([()])/g, "")})`, "gi"));
    return (
      <span>
        {parts.map((part, i) => (
          <span key={i} style={part.toLowerCase() === term.toLowerCase() ? { fontWeight: "bold" } : {}}>
            {part}
          </span>
        ))}
      </span>
    );
  };

  const Option = (props) => {
    const userInput = props?.selectProps?.inputValue || "";
    const option = props?.data;
    const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
    const newProps = { ...props, innerProps: rest };

    const oshaData = getOSHAData(option.sic);
    const displaySICName = oshaData ? oshaData.sic_name : option.sic;
    const displaySICDescription = oshaData ? oshaData.sic_description : option.sicDescription;
    const displaySICExamples = oshaData ? oshaData.sic_examples : null;

    return (
      <components.Option {...newProps} className={bem("customOption")}>
        <Div pb={2}>
          <BodyMedium weight="bold" py={2}>
            {highlightMatchingTerm(option.naicsDescription, userInput)} ({highlightMatchingTerm(option.naics, userInput)})
          </BodyMedium>
          <BodyText className={bem("description")}>
            {highlightMatchingTerm(displaySICName, userInput)} – {highlightMatchingTerm(displaySICDescription, userInput)}
          </BodyText>
          {displaySICExamples && (
            <Div py={3}>
              <BodyText className={bem("examples")}>
                <u>Examples</u>: <em>{highlightMatchingTerm(displaySICExamples, userInput)}</em>
              </BodyText>
            </Div>
          )}
        </Div>
      </components.Option>
    );
  };

  const NoOptionsMessage = (props) => (
    <components.NoOptionsMessage {...props}>
      <BodyText>We weren't able to find any matches; please change your keywords to be more generic.</BodyText>
    </components.NoOptionsMessage>
  );

  const formatLabel = (option) => `${option.naicsDescription} (${option.naics})`;

  const SingleValue = (props) => <components.SingleValue {...props}>{props.data.naics}</components.SingleValue>;

  const customResults = (option, rawInput = "") => {
    const { naics, naicsDescription, sic, sicDescription } = option.data;
    const input = rawInput.toLowerCase();
    if (selectedOption) return true;
    if ([1, 2].includes(rawInput.length)) return false;

    const oshaData = getOSHAData(sic);
    const osSICDescription = oshaData ? oshaData.sic_description : sicDescription;
    const osSICExamples = oshaData ? oshaData.sic_examples : "";

    return (
      naics.toLowerCase().includes(input) ||
      naicsDescription.toLowerCase().includes(input) ||
      (sic && sic.toLowerCase().includes(input)) ||
      (osSICDescription && osSICDescription.toLowerCase().includes(input)) ||
      (osSICExamples && osSICExamples.toLowerCase().includes(input)) ||
      formatLabel(option.data).toLowerCase().includes(input)
    );
  };

  const searchPrioritized = (optionsList) => {
    const input = inputValue.toLowerCase();
    const filteredByNAICS = optionsList.filter((option) => option.naics.toLowerCase().includes(input));
    const filteredByDescription = optionsList.filter((option) => option.naicsDescription.toLowerCase().includes(input));
    const filteredBySIC = optionsList.filter((option) => option.sic && option.sic.toLowerCase().includes(input));
    const filteredByOSHADescription = optionsList.filter((option) => {
      const oshaData = getOSHAData(option.sic);
      return oshaData && oshaData.sic_description && oshaData.sic_description.toLowerCase().includes(input);
    });
    const filteredByOSHAExamples = optionsList.filter((option) => {
      const oshaData = getOSHAData(option.sic);
      return oshaData && oshaData.sic_examples && oshaData.sic_examples.toLowerCase().includes(input);
    });

    return uniq([...filteredByNAICS, ...filteredByDescription, ...filteredBySIC, ...filteredByOSHADescription, ...filteredByOSHAExamples]);
  };

  const _setInputValue = (inputVal) => {
    setInputValue(inputVal);
    setOptions(searchPrioritized(naicsCodes));
  };

  const recordSelectedOption = (option) => {
    const location = "life";
    if (option) {
      AnalyticsTracking.trackEvent(`${location}_NAICS_filled`, {
        selection: option,
      });
    }
  };

  return (
    <div className={bem("")}>
      <Select
        className={bem("select")}
        classNamePrefix={bem("select")}
        isClearable
        isSearchable
        menuShouldScrollIntoView
        captureMenuScroll
        backspaceRemovesValue
        blurInputOnSelect
        onKeyDown={(e) => {
          if (e.key === "Backspace") setSelectedOption(null);
        }}
        placeholder={`e.g. "general contractor", "ice cream shop"`}
        name="company_application[naics_code]"
        options={options}
        getOptionLabel={formatLabel}
        getOptionValue={(option) => option.naics}
        components={{ Option, NoOptionsMessage, SingleValue }}
        value={selectedOption}
        onChange={(value, { action }) => {
          form.change("company_application[naics_code]", value?.naics);
          form.change("company_application[sic_code]", value?.sic); // Also update SIC code.
          recordSelectedOption(value);
          setSelectedOption(value);
          if (action === "clear") {
            _setInputValue("");
          }
        }}
        inputValue={inputValue}
        onInputChange={_setInputValue}
        filterOption={customResults}
        inputId="naics_code_select_search"
      />
    </div>
  );
};

export default NAICSCodeSelectSearch;
